import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import api from '../utils/axios';
import {
  FileText,
  Plus,
  Download,
  Edit2,
  Trash2,
  CheckCircle,
  Loader2,
  Clock,
  User,
  Eye,
  Save,
  X,
} from 'lucide-react';

const InformesPanel = ({ caso, onRefresh }) => {
  const [informes, setInformes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCrearDialog, setShowCrearDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [informeActual, setInformeActual] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [descargando, setDescargando] = useState(null);
  
  const [formData, setFormData] = useState({
    titulo: '',
    resumen_ejecutivo: '',
    analisis: '',
    conclusion: '',
    recomendaciones: '',
    alertas_incluidas: [],
    notas_adicionales: ''
  });

  const alertasDisponibles = caso?.alertas || [];

  useEffect(() => {
    if (caso?.id) {
      loadInformes();
    }
  }, [caso?.id]);

  const loadInformes = async () => {
    try {
      const res = await api.get(`/informes/caso/${caso.id}`);
      setInformes(res.data.informes || []);
    } catch (error) {
      console.error('Error cargando informes:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      resumen_ejecutivo: '',
      analisis: '',
      conclusion: '',
      recomendaciones: '',
      alertas_incluidas: [],
      notas_adicionales: ''
    });
  };

  const handleCrear = async () => {
    if (!formData.titulo) {
      toast.error('El título es requerido');
      return;
    }

    setGuardando(true);
    try {
      await api.post(`/informes/caso/${caso.id}`, formData);
      toast.success('Informe creado correctamente');
      setShowCrearDialog(false);
      resetForm();
      loadInformes();
    } catch (error) {
      toast.error('Error al crear el informe');
    } finally {
      setGuardando(false);
    }
  };

  const handleEditar = async () => {
    if (!informeActual) return;

    setGuardando(true);
    try {
      await api.put(`/informes/${informeActual.id}`, formData);
      toast.success('Informe actualizado');
      setShowEditDialog(false);
      loadInformes();
    } catch (error) {
      toast.error('Error al actualizar el informe');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (informeId) => {
    if (!window.confirm('¿Está seguro de eliminar este informe?')) return;

    try {
      await api.delete(`/informes/${informeId}`);
      toast.success('Informe eliminado');
      loadInformes();
    } catch (error) {
      toast.error('Error al eliminar el informe');
    }
  };

  const handleDescargarPDF = async (informe) => {
    setDescargando(informe.id);
    try {
      const response = await api.get(`/informes/${informe.id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `informe_${caso?.cuit || 'caso'}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al descargar el PDF');
    } finally {
      setDescargando(null);
    }
  };

  const handleFinalizar = async (informeId) => {
    if (!window.confirm('¿Finalizar el informe? No podrá editarse después.')) return;

    try {
      await api.post(`/informes/${informeId}/finalizar`);
      toast.success('Informe finalizado');
      loadInformes();
    } catch (error) {
      toast.error('Error al finalizar el informe');
    }
  };

  const abrirEditar = (informe) => {
    setInformeActual(informe);
    setFormData({
      titulo: informe.titulo || '',
      resumen_ejecutivo: informe.resumen_ejecutivo || '',
      analisis: informe.analisis || '',
      conclusion: informe.conclusion || '',
      recomendaciones: informe.recomendaciones || '',
      alertas_incluidas: informe.alertas_incluidas || [],
      notas_adicionales: informe.notas_adicionales || ''
    });
    setShowEditDialog(true);
  };

  const toggleAlerta = (alerta) => {
    const existe = formData.alertas_incluidas.some(a => a.alerta_id === alerta.id);
    if (existe) {
      setFormData({
        ...formData,
        alertas_incluidas: formData.alertas_incluidas.filter(a => a.alerta_id !== alerta.id)
      });
    } else {
      setFormData({
        ...formData,
        alertas_incluidas: [...formData.alertas_incluidas, {
          alerta_id: alerta.id,
          tipo: alerta.tipo,
          severidad: alerta.severidad,
          incluir_movimientos: false
        }]
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="animate-spin text-blue-500 mr-2" size={24} />
        <span className="text-slate-600">Cargando informes...</span>
      </div>
    );
  }

  const renderFormulario = (isEdit = false) => (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      <div>
        <Label className="font-medium">Título del Informe *</Label>
        <Input
          value={formData.titulo}
          onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
          placeholder="Ej: Análisis de operaciones sospechosas - Cliente X"
          className="mt-1"
        />
      </div>

      <div>
        <Label className="font-medium">Resumen Ejecutivo</Label>
        <Textarea
          value={formData.resumen_ejecutivo}
          onChange={(e) => setFormData({ ...formData, resumen_ejecutivo: e.target.value })}
          placeholder="Resumen breve de los hallazgos principales..."
          rows={3}
          className="mt-1"
        />
      </div>

      <div>
        <Label className="font-medium">Análisis</Label>
        <Textarea
          value={formData.analisis}
          onChange={(e) => setFormData({ ...formData, analisis: e.target.value })}
          placeholder="Detalle del análisis realizado..."
          rows={4}
          className="mt-1"
        />
      </div>

      {/* Selección de alertas */}
      <div>
        <Label className="font-medium mb-2 block">Alertas a Incluir en el Informe</Label>
        {alertasDisponibles.length > 0 ? (
          <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3 bg-slate-50">
            {alertasDisponibles.map((alerta, idx) => {
              const isSelected = formData.alertas_incluidas.some(a => a.alerta_id === alerta.id);
              return (
                <div
                  key={alerta.id || idx}
                  className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                    isSelected ? 'bg-blue-100 border border-blue-300' : 'bg-white hover:bg-slate-100'
                  }`}
                  onClick={() => toggleAlerta(alerta)}
                >
                  <Checkbox checked={isSelected} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">{alerta.tipo}</p>
                    <p className="text-xs text-slate-500">{alerta.descripcion?.substring(0, 60)}...</p>
                  </div>
                  <Badge variant="outline" className={`text-xs ${
                    alerta.severidad === 'CRITICA' ? 'bg-red-50 text-red-700' :
                    alerta.severidad === 'ALTA' ? 'bg-orange-50 text-orange-700' :
                    alerta.severidad === 'MEDIA' ? 'bg-amber-50 text-amber-700' :
                    'bg-slate-50 text-slate-700'
                  }`}>
                    {alerta.severidad}
                  </Badge>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500 italic">No hay alertas disponibles</p>
        )}
        <p className="text-xs text-slate-400 mt-1">
          {formData.alertas_incluidas.length} alerta(s) seleccionada(s)
        </p>
      </div>

      <div>
        <Label className="font-medium">Conclusión</Label>
        <Textarea
          value={formData.conclusion}
          onChange={(e) => setFormData({ ...formData, conclusion: e.target.value })}
          placeholder="Conclusiones del análisis..."
          rows={3}
          className="mt-1"
        />
      </div>

      <div>
        <Label className="font-medium">Recomendaciones</Label>
        <Textarea
          value={formData.recomendaciones}
          onChange={(e) => setFormData({ ...formData, recomendaciones: e.target.value })}
          placeholder="Recomendaciones y próximos pasos..."
          rows={3}
          className="mt-1"
        />
      </div>

      <div>
        <Label className="font-medium">Notas Adicionales</Label>
        <Textarea
          value={formData.notas_adicionales}
          onChange={(e) => setFormData({ ...formData, notas_adicionales: e.target.value })}
          placeholder="Cualquier otra información relevante..."
          rows={2}
          className="mt-1"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">Informes del Caso</h3>
        <Button
          onClick={() => {
            resetForm();
            setShowCrearDialog(true);
          }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus size={16} className="mr-2" />
          Nuevo Informe
        </Button>
      </div>

      {informes.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <FileText size={48} className="mx-auto mb-4 opacity-50" />
          <p>No hay informes para este caso</p>
          <p className="text-sm mt-2">Cree un informe para documentar el análisis y conclusiones</p>
        </div>
      ) : (
        <div className="space-y-3">
          {informes.map((informe) => (
            <Card key={informe.id} className={`p-4 border-l-4 ${
              informe.estado === 'FINALIZADO' ? 'border-l-green-500 bg-green-50/50' : 'border-l-blue-500'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <FileText size={18} className="text-slate-600" />
                    <h4 className="font-medium text-slate-800">{informe.titulo}</h4>
                    <Badge variant="outline" className={`text-xs ${
                      informe.estado === 'FINALIZADO' 
                        ? 'bg-green-100 text-green-700 border-green-300' 
                        : 'bg-blue-100 text-blue-700 border-blue-300'
                    }`}>
                      {informe.estado}
                    </Badge>
                  </div>
                  {informe.resumen_ejecutivo && (
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                      {informe.resumen_ejecutivo}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <User size={12} />
                      {informe.autor_nombre || informe.autor_email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {new Date(informe.fecha_creacion).toLocaleDateString('es-AR')}
                    </span>
                    <span>
                      {informe.alertas_incluidas?.length || 0} alertas incluidas
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDescargarPDF(informe)}
                    disabled={descargando === informe.id}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    {descargando === informe.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Download size={14} />
                    )}
                  </Button>
                  {informe.estado !== 'FINALIZADO' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => abrirEditar(informe)}
                        className="text-slate-600"
                      >
                        <Edit2 size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleFinalizar(informe.id)}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        <CheckCircle size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEliminar(informe.id)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog: Crear Informe */}
      <Dialog open={showCrearDialog} onOpenChange={setShowCrearDialog}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nuevo Informe del Caso</DialogTitle>
            <DialogDescription>
              Complete los campos para generar un informe. Podrá descargarlo como PDF.
            </DialogDescription>
          </DialogHeader>
          
          {renderFormulario()}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCrearDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCrear}
              disabled={guardando || !formData.titulo}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {guardando ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Crear Informe
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar Informe */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Informe</DialogTitle>
            <DialogDescription>
              Modifique los campos del informe. Los cambios se guardarán automáticamente.
            </DialogDescription>
          </DialogHeader>
          
          {renderFormulario(true)}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleEditar}
              disabled={guardando}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {guardando ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Guardar Cambios
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InformesPanel;
